/**
 * The page's own `<script id="aqx-i18n">` block, read once.
 *
 * Split out of {@link ./payload} so that {@link ./bundled-registry} can consult it without importing
 * `page-dictionary` back — that would close an import cycle. This module depends on nothing but the types.
 *
 * Browser-only by design, and it memoises: the browser has ONE page, the server has all of them. See
 * {@link ./page-dictionary} for why module state is forbidden on the server side of this package.
 */
/** The id of the single `<script type="application/json">` block the page carries. */
export const I18N_SCRIPT_ID = 'aqx-i18n';
let cached;
/**
 * Read the page's block in the BROWSER. Returns null when there is no block — an older page, a test
 * harness, an island mounted outside a Layout — and callers then fall back to the bundled dictionary.
 *
 * Total: a malformed or truncated block returns null rather than throwing, because this runs inside island
 * hydration where an exception unmounts the root and takes the buy box off the page with it.
 */
export function readPageTexts(doc) {
    if (cached !== undefined && doc === undefined)
        return cached;
    const target = doc ?? (typeof document === 'undefined' ? null : document);
    if (!target)
        return null;
    let parsed = null;
    try {
        const raw = target.getElementById(I18N_SCRIPT_ID)?.textContent;
        if (raw) {
            const value = JSON.parse(raw);
            if (value &&
                typeof value.locale === 'string' &&
                typeof value.overrides === 'object' &&
                value.overrides !== null) {
                const carried = value.texts;
                parsed = {
                    locale: value.locale,
                    overrides: value.overrides,
                    // Anything that is not an object is dropped rather than trusted: this block is parsed from the
                    // DOM, and a component handed a number would render a key.
                    texts: typeof carried === 'object' && carried !== null && !Array.isArray(carried)
                        ? carried
                        : undefined,
                };
            }
        }
    }
    catch {
        parsed = null;
    }
    if (doc === undefined)
        cached = parsed;
    return parsed;
}
/** Drop the memo — for tests, which mount several pages in one process. */
export function resetPageTextsCache() {
    cached = undefined;
}
//# sourceMappingURL=page-block.js.map